import { Shimmer } from '@/components/ai-elements/shimmer';
import { useEffect, useRef, useState } from 'react';
import {
  ChevronLeftIcon,
  ChevronRightIcon,
  CopyIcon,
  DownloadIcon,
} from 'lucide-react';
import type { ChatDocumentDTO } from '@shared/schemas/chat';
import { toast } from 'sonner';
import {
  Artifact,
  ArtifactAction,
  ArtifactActions,
  ArtifactClose,
  ArtifactContent,
  ArtifactDescription,
  ArtifactHeader,
  ArtifactTitle,
} from '@/components/ai-elements/artifact';
import type { ActiveSourceReference } from '../chat-experience';
import { formatSourceLocation, getSourceLocation } from '../chat-document-sources';
import { Button } from '@/components/ui/button';
import { getChatErrorMessage } from '../chat-errors';
import {
  formatStreamingMetricsLabel,
  getDocumentContent,
  getDocumentTitle,
  getDocumentWordCount,
} from '../document-artifact';
import { useChatDocumentDocxPreview } from '../hooks/useChatDocumentDocxPreview';
import { isCodegenChatDocument, isPreviewUnavailableMediaType } from '../codegen-document';
import { downloadRemoteFile, saveBlobAsFile } from '../download-remote-file';
import { GeneratedDocumentPanel } from '../document-canvas/GeneratedDocumentPanel';
import { ChatDocumentPreview } from './ChatDocumentPreview';
import { CodegenDocxPanelPreview } from './CodegenDocxPanelPreview';
import { DOCX_MIME_TYPE } from './document-viewer/document-viewer-registry';
import { GeneratedFilePreviewUnavailable } from './GeneratedFilePreviewUnavailable';
import { parseChatAttachmentId } from '../chat-attachment-source';
import { ChatAttachmentPanel } from './ChatAttachmentPanel';
import { DocumentViewer } from './document-viewer/DocumentViewer';
import { ChatSidePanelShell } from './ChatSidePanelShell';
import { ChatDocumentKindIcon } from './ChatDocumentKindIcon';
import { useIsMobile } from '@/hooks/use-mobile';

type DocxPreviewBinding = {
  blob: Blob | null;
  blobUrl: string | null;
  downloadFilename: string | null;
  downloadUrl: string | null;
  error: string | null;
  retry: () => void;
  status: 'idle' | 'generating' | 'ready' | 'error';
};

type ChatDocumentPanelProps = {
  activeSourceReference: ActiveSourceReference | null;
  activeSourceReferences: ActiveSourceReference[];
  conversationId?: string;
  document: ChatDocumentDTO | null;
  isOpen: boolean;
  onOpenChange: (open: boolean) => void;
  /** Moves the viewer to another cited passage of the same document. */
  onSelectSourceReference?: (reference: ActiveSourceReference) => void;
};

function sanitizeFilename(value: string) {
  return value
    .toLowerCase()
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .replace(/[^a-z0-9_-]+/g, '-')
    .replace(/^-+|-+$/g, '')
    .slice(0, 64) || 'document';
}

function hasDirectSourceDocument(document: ChatDocumentDTO) {
  return Boolean(document.sourceUrl);
}

function isDirectSourceView(document: ChatDocumentDTO) {
  if (
    isCodegenChatDocument(document)
    && document.mediaType === DOCX_MIME_TYPE
    && document.sourceUrl
  ) {
    return true;
  }

  return document.status === 'ready' && hasDirectSourceDocument(document);
}

function isDocxPreviewView(document: ChatDocumentDTO, docxPreview: DocxPreviewBinding) {
  return document.status === 'ready' && docxPreview.status !== 'idle';
}

/** Document de génération sans aperçu possible (classeur ou présentation de la voie script). */
function isUnpreviewableGeneratedFile(document: ChatDocumentDTO) {
  return isCodegenChatDocument(document) && isPreviewUnavailableMediaType(document.mediaType);
}

function PanelHeader({
  canCopy,
  document,
  isDownloadDisabled,
  onClose,
  onCopy,
  onDownload,
}: {
  canCopy: boolean;
  document: ChatDocumentDTO;
  isDownloadDisabled: boolean;
  onClose: () => void;
  onCopy: () => void;
  onDownload: () => void;
}) {
  const title = getDocumentTitle(document);

  return (
    <ArtifactHeader className="h-[3.25rem] border-border/50 bg-muted/20 px-4">
      <div className="flex min-w-0 items-center gap-3">
        <ChatDocumentKindIcon document={document} size="xs" />
        <div className="min-w-0">
          <ArtifactTitle className="truncate text-sm font-semibold">{title}</ArtifactTitle>
          <ArtifactDescription className="truncate text-xs">
            {document.status === 'streaming'
              ? formatStreamingMetricsLabel(getDocumentWordCount(document))
              : (document.filename ?? document.mediaType ?? 'Document conversationnel')}
          </ArtifactDescription>
        </div>
      </div>

      <div className="flex items-center gap-1">
        <ArtifactActions>
          {canCopy ? (
            <ArtifactAction
              icon={CopyIcon}
              label="Copier le document"
              onClick={onCopy}
              tooltip="Copier"
            />
          ) : null}
          <ArtifactAction
            disabled={isDownloadDisabled}
            icon={DownloadIcon}
            label="Télécharger le document"
            onClick={onDownload}
            tooltip="Télécharger"
          />
        </ArtifactActions>
        <ArtifactClose
          className="size-8 p-0 text-muted-foreground hover:text-foreground"
          onClick={onClose}
          aria-label="Fermer le panneau"
        />
      </div>
    </ArtifactHeader>
  );
}

function PanelBody({
  activeSourceReference,
  activeSourceReferences,
  docxPreview,
  document,
  isDownloading,
  onDownload,
  onRefreshSource,
}: {
  activeSourceReference: ActiveSourceReference | null;
  activeSourceReferences: ActiveSourceReference[];
  docxPreview: DocxPreviewBinding;
  document: ChatDocumentDTO;
  isDownloading: boolean;
  onDownload: () => void;
  onRefreshSource?: () => Promise<string | null>;
}) {
  const containerRef = useRef<HTMLDivElement>(null);
  const userScrolledRef = useRef(false);
  const content = getDocumentContent(document).trim();
  const showDocxPreview = isDocxPreviewView(document, docxPreview);
  const isSourceDocument = isDirectSourceView(document);

  useEffect(() => {
    if (document.status !== 'streaming' || userScrolledRef.current) {
      return;
    }

    const element = containerRef.current;
    element?.scrollTo({ top: element.scrollHeight });
  }, [content, document.status]);

  if (
    isCodegenChatDocument(document)
    && document.mediaType === DOCX_MIME_TYPE
    && document.sourceUrl
  ) {
    return (
      <ArtifactContent className="relative flex-1 overflow-hidden bg-[#f1f3f5] dark:bg-neutral-900 p-0">
        <CodegenDocxPanelPreview
          filename={document.filename ?? document.title}
          onRefreshSource={onRefreshSource}
          sourceUrl={document.sourceUrl}
        />
      </ArtifactContent>
    );
  }

  if (isUnpreviewableGeneratedFile(document)) {
    // Les lecteurs XLSX/PPTX interrogent l'API par identifiant de document indexé : un
    // `docgen:<uuid>` n'y existe pas. Mieux vaut un état explicite qu'une erreur API.
    return (
      <ArtifactContent className="relative flex-1 overflow-hidden bg-[#f1f3f5] dark:bg-neutral-900 p-0">
        <GeneratedFilePreviewUnavailable
          filename={document.filename ?? document.title}
          isDownloading={isDownloading}
          mediaType={document.mediaType}
          onDownload={onDownload}
        />
      </ArtifactContent>
    );
  }

  if (isSourceDocument) {
    return (
      <ArtifactContent className="relative flex-1 overflow-hidden bg-[#f1f3f5] dark:bg-neutral-900 p-0">
        <DocumentViewer
          citation={activeSourceReference ? {
            anchor: activeSourceReference.anchor ?? null,
            excerpt: activeSourceReference.excerpt,
            reference: activeSourceReference,
          } : null}
          documentId={document.documentId}
          filename={document.filename ?? document.title}
          mediaType={document.mediaType ?? 'application/octet-stream'}
          onClose={() => undefined}
          sourceUrl={document.sourceUrl}
        />
      </ArtifactContent>
    );
  }

  if (showDocxPreview) {
    return (
      <ArtifactContent className="relative flex-1 overflow-hidden bg-[#f1f3f5] dark:bg-neutral-900 p-0">
        <ChatDocumentPreview
          activeReference={activeSourceReference}
          docxPreview={{
            blob: docxPreview.blob,
            error: docxPreview.error,
            onRetry: docxPreview.retry,
            status: docxPreview.status,
          }}
          document={document}
          sourceReferences={activeSourceReferences}
          variant="panel"
        />
      </ArtifactContent>
    );
  }

  return (
    <ArtifactContent className="relative flex-1 overflow-hidden bg-background p-0">
      <div
        ref={containerRef}
        className="relative h-full overflow-y-auto bg-muted/35 px-6 py-6"
        onScroll={() => {
          const element = containerRef.current;

          if (!element) {
            return;
          }

          const atBottom = element.scrollHeight - element.scrollTop - element.clientHeight < 40;
          userScrolledRef.current = !atBottom;
        }}
      >
        <div className="mx-auto flex h-full w-full max-w-[48rem]">
          <ChatDocumentPreview
            activeReference={activeSourceReference}
            className="h-full w-full shadow-[0_24px_60px_rgba(15,23,42,0.08)]"
            content={content}
            document={document}
            sourceReferences={activeSourceReferences}
            variant="panel"
          />
        </div>
      </div>
    </ArtifactContent>
  );
}

/** « Passage 2 / 3 · p. 12 » with previous/next, for a document cited several times. */
function CitedPassageNavigator({
  activeReference,
  onSelect,
  references,
}: {
  activeReference: ActiveSourceReference | null;
  onSelect: (reference: ActiveSourceReference) => void;
  references: ActiveSourceReference[];
}) {
  const index = Math.max(0, references.findIndex((reference) => reference.referenceId === activeReference?.referenceId));
  const location = getSourceLocation(references[index] ?? null);
  const goTo = (nextIndex: number) => {
    const reference = references[nextIndex];
    if (reference) onSelect(reference);
  };

  return (
    <div
      className="flex items-center justify-between gap-2 border-b border-border/40 bg-amber-50/60 px-4 py-1.5 text-xs text-amber-950 dark:bg-amber-950/20 dark:text-amber-100"
      data-testid="cited-passage-navigator"
    >
      <span aria-live="polite" className="tabular-nums">
        Passage cité {index + 1} / {references.length}
        {location ? ` · ${formatSourceLocation(location, 'short')}` : ''}
      </span>
      <div className="flex items-center gap-1">
        <Button
          aria-label="Passage cité précédent"
          className="size-7"
          disabled={index === 0}
          onClick={() => goTo(index - 1)}
          size="icon"
          type="button"
          variant="ghost"
        >
          <ChevronLeftIcon className="size-4" />
        </Button>
        <Button
          aria-label="Passage cité suivant"
          className="size-7"
          disabled={index >= references.length - 1}
          onClick={() => goTo(index + 1)}
          size="icon"
          type="button"
          variant="ghost"
        >
          <ChevronRightIcon className="size-4" />
        </Button>
      </div>
    </div>
  );
}

function DocumentPanelArtifact({
  activeSourceReference,
  activeSourceReferences,
  conversationId,
  document,
  isOpen,
  onClose,
  onRefreshSource,
  onSelectSourceReference,
}: {
  activeSourceReference: ActiveSourceReference | null;
  activeSourceReferences: ActiveSourceReference[];
  conversationId?: string;
  document: ChatDocumentDTO;
  isOpen: boolean;
  onClose: () => void;
  /** Nouvelle URL de téléchargement d'un fichier généré (les URL présignées expirent). */
  onRefreshSource?: () => Promise<string | null>;
  onSelectSourceReference?: (reference: ActiveSourceReference) => void;
}) {
  const [isDownloading, setIsDownloading] = useState(false);
  const docxPreview = useChatDocumentDocxPreview({
    conversationId,
    document,
    enabled: isOpen,
  });

  async function handleCopy() {
    const contentToCopy = getDocumentContent(document).trim();

    if (!contentToCopy) {
      toast.info('Aucun contenu à copier pour le moment.');
      return;
    }

    try {
      await navigator.clipboard.writeText(contentToCopy);
      toast.success('Contenu du document copié.');
    } catch {
      toast.error('Impossible de copier le contenu du document.');
    }
  }

  function handleMarkdownDownload() {
    const contentToDownload = getDocumentContent(document).trim();

    if (!contentToDownload) {
      toast.info('Aucun contenu à télécharger pour le moment.');
      return;
    }

    const filename = `${sanitizeFilename(getDocumentTitle(document))}.md`;
    const blob = new Blob([contentToDownload], { type: 'text/markdown;charset=utf-8' });
    const url = URL.createObjectURL(blob);
    const link = window.document.createElement('a');
    link.href = url;
    link.download = filename;
    window.document.body.append(link);
    link.click();
    link.remove();
    URL.revokeObjectURL(url);
  }

  async function handleDownloadCurrentView() {
    if (isDownloading) {
      return;
    }

    if (isDirectSourceView(document) && document.sourceUrl) {
      setIsDownloading(true);

      try {
        const filename = document.filename ?? `${sanitizeFilename(getDocumentTitle(document))}.bin`;
        // URL présignée du stockage pour un fichier généré ou une pièce jointe (sans cookie) ;
        // route d'API sinon.
        const isPresignedFile = isCodegenChatDocument(document) || document.kind === 'attachment';
        try {
          await downloadRemoteFile(document.sourceUrl, filename, { withCredentials: !isPresignedFile });
        } catch (downloadError) {
          const freshUrl = isPresignedFile ? await onRefreshSource?.() : null;
          if (!freshUrl) {
            throw downloadError;
          }
          await downloadRemoteFile(freshUrl, filename);
        }
        toast.success('Téléchargement lancé.');
      } catch (error) {
        toast.error(getChatErrorMessage(error));
      } finally {
        setIsDownloading(false);
      }

      return;
    }

    if (isDocxPreviewView(document, docxPreview)) {
      if (docxPreview.status === 'ready' && docxPreview.blob && docxPreview.downloadFilename) {
        saveBlobAsFile(docxPreview.blob, docxPreview.downloadFilename);
        toast.success('Téléchargement lancé.');
        return;
      }

      toast.info('L’aperçu Word est encore en préparation.');
      return;
    }

    handleMarkdownDownload();
    toast.success('Téléchargement lancé.');
  }

  const canCopy = !isCodegenChatDocument(document) || getDocumentContent(document).trim().length > 0;
  const isDownloadDisabled = isDownloading
    || document.status === 'streaming'
    || (isDocxPreviewView(document, docxPreview) && docxPreview.status === 'generating');

  return (
    <Artifact className="h-full rounded-none border-none bg-sidebar shadow-none">
      <PanelHeader
        canCopy={canCopy}
        document={document}
        isDownloadDisabled={isDownloadDisabled}
        onClose={onClose}
        onCopy={() => void handleCopy()}
        onDownload={() => void handleDownloadCurrentView()}
      />
      {docxPreview.status === 'generating' ? (
        <div className="flex items-center gap-2 border-b border-border/40 bg-muted/20 px-4 py-2 text-xs text-muted-foreground">
          <Shimmer>Préparation de l’aperçu Word…</Shimmer>
        </div>
      ) : null}
      {isDownloading ? (
        <div className="flex items-center gap-2 border-b border-border/40 bg-muted/20 px-4 py-2 text-xs text-muted-foreground">
          <Shimmer>Téléchargement en cours…</Shimmer>
        </div>
      ) : null}
      {onSelectSourceReference && isDirectSourceView(document) && activeSourceReferences.length > 1 ? (
        <CitedPassageNavigator
          activeReference={activeSourceReference}
          onSelect={onSelectSourceReference}
          references={activeSourceReferences}
        />
      ) : null}
      <PanelBody
        activeSourceReference={activeSourceReference}
        activeSourceReferences={activeSourceReferences}
        docxPreview={docxPreview}
        document={document}
        isDownloading={isDownloading}
        onDownload={() => void handleDownloadCurrentView()}
        onRefreshSource={onRefreshSource}
      />
    </Artifact>
  );
}

function PanelContent({
  activeSourceReference,
  activeSourceReferences,
  conversationId,
  document,
  isOpen,
  onClose,
  onSelectSourceReference,
}: {
  activeSourceReference: ActiveSourceReference | null;
  activeSourceReferences: ActiveSourceReference[];
  conversationId?: string;
  document: ChatDocumentDTO;
  isOpen: boolean;
  onClose: () => void;
  onSelectSourceReference?: (reference: ActiveSourceReference) => void;
}) {
  const artifactProps = {
    activeSourceReference,
    activeSourceReferences,
    conversationId,
    isOpen,
    onClose,
    onSelectSourceReference,
  };

  if (conversationId && isCodegenChatDocument(document)) {
    // Document de génération : canvas (moteur blocks) ou fichier de la voie script.
    return (
      <GeneratedDocumentPanel
        conversationId={conversationId}
        document={document}
        onClose={onClose}
        renderFileDocument={(fileDocument, onRefreshSource) => (
          <DocumentPanelArtifact {...artifactProps} document={fileDocument} onRefreshSource={onRefreshSource} />
        )}
      />
    );
  }

  const attachmentId = parseChatAttachmentId(document);
  if (attachmentId) {
    return (
      <ChatAttachmentPanel
        attachmentId={attachmentId}
        document={document}
        onClose={onClose}
        renderFileDocument={(fileDocument, onRefreshSource) => (
          <DocumentPanelArtifact {...artifactProps} document={fileDocument} onRefreshSource={onRefreshSource} />
        )}
      />
    );
  }

  return <DocumentPanelArtifact {...artifactProps} document={document} />;
}

export function ChatDocumentPanel({
  activeSourceReference,
  activeSourceReferences,
  conversationId,
  document,
  isOpen,
  onOpenChange,
  onSelectSourceReference,
}: ChatDocumentPanelProps) {
  const isMobile = useIsMobile();
  const isVisible = Boolean(document) && isOpen;

  if (isMobile) {
    if (!isVisible || !document) {
      return null;
    }

    return (
      <div className="fixed inset-0 z-50 flex flex-col overflow-hidden bg-background">
        <PanelContent
          key={document.documentId}
          activeSourceReference={activeSourceReference}
          activeSourceReferences={activeSourceReferences}
          conversationId={conversationId}
          document={document}
          isOpen={isVisible}
          onClose={() => onOpenChange(false)}
          onSelectSourceReference={onSelectSourceReference}
        />
      </div>
    );
  }

  return (
    <ChatSidePanelShell isOpen={isVisible} testId="document-panel">
      {document ? (
        <PanelContent
          key={document.documentId}
          activeSourceReference={activeSourceReference}
          activeSourceReferences={activeSourceReferences}
          conversationId={conversationId}
          document={document}
          isOpen={isVisible}
          onClose={() => onOpenChange(false)}
          onSelectSourceReference={onSelectSourceReference}
        />
      ) : null}
    </ChatSidePanelShell>
  );
}
